/** biome-ignore-all lint/style/useNamingConvention: camel case */

import cx from 'classnames';
import {useSelector} from 'react-redux';
import {AMAZON_AF_DEFAULT_DISK_IMAGE} from '~/common/affiliate/amazon';
import {GuestGiftlist} from '~/common/components/GuestGiftlist/GuestGiftlist';
import {
  getGiftKeywordAmazonSearchLink,
  hasGiftKeywords,
  selectEvent,
  selectGiftKeywords,
} from '~/common/gift-keywords/utils';
import {eviteVendors, getDiscDisplayText, getWalmartURL} from '~/common/vendors/vendors';
import {VendorDisc} from '~/ViewInvitation/common/VendorDisc';
import {VendorRect} from '~/ViewInvitation/common/VendorRect';
import {RegistryModule} from '~/ViewInvitation/routes/Landing/RegistryModule';
import {isPreview} from '~/ViewInvitation/utils';
import {Divider} from '~library/atoms/Divider';
import {Typography} from '~library/atoms/Typography';
import {useIsLargeAndUp, useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {DiamondIcon} from '~library/svg/DiamondIcon';

import './GiftingModule.sass';

function hasWidget(widgetType, event = window.evite_event) {
  // Copied from classic interface /assets/js/view_invitation/utils.js
  if (!event) return false;
  const widgets = event.widgets || [];
  return widgets.some((w) => w.widget_type === widgetType);
}

function getShowGiftKeywords(event, keywords) {
  return (
    !window.has_gift_registries &&
    !hasWidget('sponsored_registry', event) &&
    hasGiftKeywords(keywords)
  );
}

const preview = isPreview();

export const KeywordList = (props) => {
  const {keywords} = props;
  const filledKeywords = [];
  const pushIfNotEmpty = (lst, str) => {
    if (str.length > 0) lst.push(str);
  };
  keywords.forEach((keyword) => {
    pushIfNotEmpty(filledKeywords, keyword);
  });

  const filledKeywordsWithSetIndex = filledKeywords.map((kw, index) => ({keyword: kw, index}));
  return (
    <div className="view-invitation__gifting-module__gift-ideas__keyword-list">
      {filledKeywordsWithSetIndex.map(({keyword, index}) => (
        <a
          key={`${keyword}_${index}`}
          target="_blank"
          data-qa-id={`gift_idea_${index}`}
          href={preview ? undefined : getGiftKeywordAmazonSearchLink(keyword)}
          rel="noreferrer"
          className="button large secondary view-invitation__gifting-module__gift-idea"
        >
          {keyword}
        </a>
      ))}
    </div>
  );
};

export const GuestKeywordsWidget = () => {
  if (!hasGiftKeywords()) return '';

  const keywords = window.evite_event?.gift_keywords || [];
  return (
    <div className="view-invitation__gifting-module__gift-keywords-wrapper">
      <Typography
        variant="header3"
        color="charcoal"
        classNames="view-invitation__gifting-module__gift-keywords-title"
      >
        {`Shop ${window.evite_event?.host_name || ''}'s interests for gift ideas on`}
        <img
          src={AMAZON_AF_DEFAULT_DISK_IMAGE}
          className="view-invitation__gifting-module__shop-on-amazon-image"
          alt="Amazon"
        />
      </Typography>
      <KeywordList keywords={keywords} />
    </div>
  );
};

// TODO: Combine the following components and have a single sass file:
// - ViewInvitation/routes/Landing/GiftingModule/GiftingModule.sass
// - InvitationMaker/OneStepCreatePage/components/Options/components/GiftIdeas.js
export const GiftingModule = (props) => {
  const {keywords} = useSelector(selectGiftKeywords);
  const event = useSelector(selectEvent);
  const isLargeAndUp = useIsLargeAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const {vendorDiscDataQaId = 'af-invite-disc', readonly, forceShowCTA, connectCTA} = props;
  let {showGiftKeywords = true} = props;

  const isPremium = useSelector((state) => (state.details || state.eventDetails).isPremium);
  const giftingData = useSelector(
    (state) => state.gifting ?? {url: '', iconUrl: '', vendorName: '', title: '', note: ''}
  );
  const registries =
    useSelector((state) =>
      'giftingOptions' in state && state.giftingOptions?.gift_registry_urls.length === 0
        ? null
        : (state.registry?.registries ?? null)
    ) || [];
  showGiftKeywords = showGiftKeywords && getShowGiftKeywords(event, keywords);

  const showGuestGiftlist =
    window.evite_event?.gift_list_url && window.evite_event?.gift_registry_urls.length === 0;

  const displayText = getDiscDisplayText(event);
  const walmartURL = getWalmartURL(event);
  const walmartVendor = eviteVendors.find(({name}) => name === 'Walmart');

  const dividerMargin = isLargishAndUp ? '2.5rem' : '2rem';

  return (
    <div className="gifting-submodule__wrapper">
      <Typography variant="label2" data-qa-id="gifting-module-title" color="pebble">
        Gifting
      </Typography>
      <RegistryModule {...props} />
      {showGuestGiftlist && (
        <>
          {registries?.length > 0 && (
            <Divider marginBottom={dividerMargin} marginTop={dividerMargin} />
          )}
          <div className="giftlist-card gifting__submodule">
            <GuestGiftlist />
          </div>
        </>
      )}
      {showGiftKeywords
        ? <>
            {(registries?.length > 0 || showGuestGiftlist) && (
              <Divider marginTop={dividerMargin} marginBottom={dividerMargin} />
            )}
            <GuestKeywordsWidget />
          </>
        : null}
      {event.allowGifts && (
        <div
          className={cx('submodule-gifting', {
            'with-registries': registries?.length > 0 || showGuestGiftlist || showGiftKeywords,
          })}
          id="submodule-gifting"
        >
          <div>
            {((preview && !readonly) || forceShowCTA) && (
              <div className="view-invitation__preview-cta multi">
                <button
                  className="unstyled-button view-invitation__preview-cta"
                  data-qa-id="remove-gifting"
                  id="remove-gifting"
                  ref={(elem) => {
                    connectCTA?.('remove-gifting', elem);
                  }}
                  type="button"
                >
                  {!isPremium && <DiamondIcon />}
                  Remove
                </button>
              </div>
            )}
            <div className="view-invitation__gifting-module__vendors">
              <Typography variant="header6" data-qa-id="gifting-module-title" semibold>
                Browse gift guides
              </Typography>
              <div
                className={cx('view-invitation__gifting-module__vendor-list', {
                  rectangles: !isLargeAndUp,
                })}
                data-qa-id="gifting-vendor-list"
              >
                <Vendor
                  registry={giftingData}
                  displayText={displayText}
                  dataQaId={`${vendorDiscDataQaId}-amazon`}
                  showAsRectangle={!isLargeAndUp}
                />
                <Vendor
                  registry={{
                    ...walmartVendor,
                    vendor_name: walmartVendor.name,
                    url: walmartURL,
                  }}
                  displayText={displayText}
                  dataQaId={`${vendorDiscDataQaId}-walmart`}
                  showAsRectangle={!isLargeAndUp}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const Vendor = ({showAsRectangle, ...props}) =>
  showAsRectangle ? <VendorRect {...props} /> : <VendorDisc {...props} />;
